import { ChangeEvent, useEffect, useState } from "react";

import { Autocomplete, Button, IconWrapper, InfoPanel, SelectEvent, TextField, Typography } from "@king-ict/eupisi-ui/components";
import { ReactComponent as ArrowRight } from "@king-ict/eupisi-ui/components/Icon/ArrowRight.svg";
import { CodeBook } from "@king-ict/eupisi-ui/models";
import { codeBookToSelectData } from "@king-ict/eupisi-ui/utils";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { CountySelect, FormCard } from "..";
import { searchHouseNumbers, searchMunicipalities, searchStreets, searchTowns } from "../../api/Geography/GeographyApi";
import { useDidUpdateEffect } from "../../hooks";
import { Address, GrandParentCodeBookPagedFilter, HierarchicalCodeBookPagedFilter } from "../../models";
import { useGetCountiesQuery } from "../../queries/codeBookQuery";




export interface AddressSelectProps {
  initialValue?: Address;
  infoValue?: Address;
  title?: string;
  children?: any;
  readOnly?: boolean,
  disabled?: boolean,
  handleChange?: (value: Address) => void,
  errorText?: string
}
export const AddressSelect = ({ initialValue, infoValue, children, title, readOnly = true, disabled = false, errorText, handleChange }: AddressSelectProps) => {
  const CreateDefaultAddress = (): Address => {
    return {
      countyId: undefined,
      county: "",
      municipalityId: undefined,
      municipality: "",
      town: "",
      townId: undefined,
      street: "",
      streetId: undefined,
      houseNumber: "",
      houseNumberId: undefined
    }
  };

  const { t } = useTranslation();
  const { data: counties } = useGetCountiesQuery();
  const [municipalities, setMunicipalities] = useState<CodeBook[]>();
  const [towns, setTowns] = useState<CodeBook[]>();
  const [streets, setStreets] = useState<CodeBook[]>()
  const [houseNumbers, setHouseNumbers] = useState<CodeBook[]>();
  const [address, setAddress] = useState<Address>(initialValue ?? CreateDefaultAddress);

  useDidUpdateEffect(() => {
    setAddress(initialValue ?? CreateDefaultAddress);
  }, [initialValue]);

  useEffect(() => {
    (async () => {
      if (address?.countyId && (disabled || readOnly)) {
        setMunicipalities([{ id: address?.municipalityId ?? 0, name: address?.municipality }]);
      }
      else if (address?.countyId && !readOnly && !disabled) {
        const search: HierarchicalCodeBookPagedFilter = {
          pageSize: 1500,
          page: 1,
          parentId: [address.countyId],
          search: "",
        };
        var result = await searchMunicipalities(search);
        setMunicipalities(result.results);
      };
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address.countyId, disabled, readOnly]);
  useEffect(() => {
    (async () => {
      if (address?.municipalityId && (disabled || readOnly)) {
        setTowns([{ id: address?.townId ?? 0, name: address?.town }]);
      }
      else if (address?.municipalityId && !readOnly && !disabled) {
        const searchTown: GrandParentCodeBookPagedFilter = {
          pageSize: 1500,
          page: 1,
          ids: [],
          parentId: address?.municipalityId ? [address?.municipalityId] : [],
          search: "",
        };
        const result = await searchTowns(searchTown);
        setTowns(result.results);
      } else
        setTowns(undefined);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address?.municipalityId, disabled, readOnly]);
  useEffect(() => {
    (async () => {
      if (address?.townId && (disabled || readOnly)) {
        setStreets([{ id: address?.streetId ?? 0, name: address?.street }]);
      }
      else if (address?.townId && !readOnly && !disabled) {
        const searchTown: GrandParentCodeBookPagedFilter = {
          pageSize: 2500,
          page: 1,
          ids: [],
          parentId: address?.townId ? [address?.townId] : [],
          search: "",
        };
        const result = await searchStreets(searchTown);
        setStreets(result.results);
      } else
        setStreets(undefined);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address?.townId, disabled, readOnly]);
  useEffect(() => {
    (async () => {
      if (address?.townId && (disabled || readOnly)) {
        setHouseNumbers([{ id: address?.houseNumberId ?? 0, name: address?.houseNumber }]);
      }
      else if (address?.streetId && !readOnly && !disabled) {
        const searchTown: HierarchicalCodeBookPagedFilter = {
          pageSize: 2500,
          page: 1,
          ids: [],
          parentId: address?.streetId ? [address?.streetId] : [],
          search: "",
        };
        const result = await searchHouseNumbers(searchTown);
        setHouseNumbers(result.results);
      } else
        setHouseNumbers(undefined);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address?.streetId, disabled, readOnly]);

  const handleCountyChange = async (e: ChangeEvent<SelectEvent>) => {

    var countyId = Number(e.target.value);
    var county = counties?.find(e => e.id === countyId)?.name ?? "";

    const newAddress: Address = {
      ...address,
      countyId: countyId,
      county: county,
      municipalityId: undefined,
      municipality: "",
      townId: undefined,
      town: "",
      streetId: undefined,
      street: "",
      houseNumberId: undefined,
      houseNumber: ""
    };
    setAddress(newAddress);
    handleChange?.(newAddress);
  }
  const handleMunicipalityChange = async (e: ChangeEvent<SelectEvent>) => {
    var municipalityId = Number(e.target.value);
    var municipality = municipalities?.find(e => e.id === municipalityId)?.name ?? "";

    const newAddress: Address = {
      ...address,
      municipalityId: municipalityId,
      municipality: municipality,
      townId: undefined,
      town: "",
      streetId: undefined,
      street: "",
      houseNumberId: undefined,
      houseNumber: ""
    }
    setAddress(newAddress);
    handleChange?.(newAddress);
  }
  const handleTownChange = async (e: ChangeEvent<SelectEvent>) => {
    var townId = Number(e.target.value);
    var town = towns?.find(e => e.id === townId)?.name ?? "";
    const newAddress: any = {
      ...address,
      townId: townId,
      town: town,
      streetId: undefined,
      street: "",
      houseNumberId: undefined,
      houseNumber: ""
    };
    setAddress(newAddress);
    handleChange?.(newAddress);
  }
  const handleStreetChange = async (e: ChangeEvent<SelectEvent>) => {
    var streetId = Number(e.target.value);
    var street = streets?.find(e => e.id === streetId)?.name ?? "";
    const newAddress: any = {
      ...address,
      streetId: streetId,
      street: street,
      houseNumberId: undefined,
      houseNumber: ""
    };
    setAddress(newAddress);
    handleChange?.(newAddress);
  }
  const handleHouseNumbeChange = async (e: ChangeEvent<SelectEvent>) => {
    var houseNumberId = Number(e.target.value);
    var houseNumber = houseNumbers?.find(e => e.id === houseNumberId)?.name ?? "";
    const newAddress: any = {
      ...address,
      houseNumberId: houseNumberId,
      houseNumber: houseNumber
    };
    setAddress(newAddress);
    handleChange?.(newAddress);
  }
  const renderOibServiceInfo = (showButton: boolean, colWidth: number, message?: string, buttonDisabled?: boolean) => {
    const effectiveColWidth = showButton ? colWidth - 1 : colWidth;
    const effectiveDisabled = buttonDisabled ?? false;
    return (
      <>
        {showButton &&
          <Col md={1}>
            <Button
              outline={true}
              size="sm"
              onClick={() => {
                setAddress(infoValue || address);
                handleChange?.(address);
              }
              }
              disabled={effectiveDisabled}
            >
              <IconWrapper color="primary">
                <ArrowRight />
              </IconWrapper>
            </Button>
          </Col>
        }
        <Col md={effectiveColWidth}>
          <Typography color="primary">
            {message}
          </Typography>
        </Col>
      </>
    )
  };

  return (
    <>
      <FormCard cardTitle={title}>
        <Row>
          <Col md="4">
            {readOnly ?
              <TextField
                label={t("common.schoolCandidateModel.countyList")}
                value={address?.county}
                readonly
                plaintext
              />
              :
              <CountySelect
                label={t("common.schoolCandidateModel.countyList")}
                name={"CountyId"}
                value={address?.countyId}
                onChange={(e) => handleCountyChange(e)}
                disabled={disabled}
                errorText={errorText && "-"}
              />}
          </Col>
          <Col md="4">
            {readOnly ?
              <TextField
                label={t("common.schoolCandidateModel.municipalityList")}
                value={address?.municipality}
                readonly
                plaintext
                errorText={errorText}
              />
              : <Autocomplete
                label={t("common.schoolCandidateModel.municipalityList")}
                name={"MunicipalityId"}
                value={address?.municipalityId}
                selectData={codeBookToSelectData(municipalities)}
                onChange={(e) => handleMunicipalityChange(e)}
                disabled={disabled || !municipalities}
                errorText={errorText && "-"}
              />}
          </Col>
          <Col md="4">
            {readOnly ?
              <TextField
                label={t("common.schoolCandidateModel.townList")}
                value={address?.town}
                readonly
                plaintext
                errorText={errorText}
              />
              : <Autocomplete
                label={t("common.schoolCandidateModel.townList")}
                name={"TownId"}
                value={address?.townId}
                selectData={codeBookToSelectData(towns)}
                onChange={(e) => handleTownChange(e)}
                disabled={disabled || !towns}
                errorText={errorText && "-"}
              />}
          </Col>
        </Row>
        {(infoValue) &&
          <Row>
            {renderOibServiceInfo(false, 4, infoValue.county)}
            {renderOibServiceInfo(false, 4, infoValue.municipality)}
            {renderOibServiceInfo(false, 4, infoValue.town)}
          </Row>
        }
        <Row>
          <Col md="4">
            {readOnly ?
              <TextField
                label={t("common.schoolCandidateModel.streetList")}
                value={address?.street}
                readonly
                plaintext
                errorText={errorText}
              />
              : <Autocomplete
                label={t("common.schoolCandidateModel.streetList")}
                name={"StreetId"}
                value={address?.streetId}
                selectData={codeBookToSelectData(streets)}
                onChange={(e) => handleStreetChange(e)}
                disabled={disabled || !streets}
                errorText={errorText && "-"}
              />}
          </Col>
          <Col md="4">
            {readOnly ?
              <TextField
                label={t("common.schoolCandidateModel.houseNumberSelector")}
                value={address?.houseNumber}
                readonly
                plaintext
                errorText={errorText}
              />
              : <Autocomplete
                label={t("common.schoolCandidateModel.houseNumberSelector")}
                name={"HouseNumberId"}
                value={address?.houseNumberId}
                selectData={codeBookToSelectData(houseNumbers)}
                onChange={(e) => handleHouseNumbeChange(e)}
                disabled={disabled || !houseNumbers}
                errorText={errorText && "-"}
              />}
          </Col>
          <Col md="4">
            <br />
            {!errorText || <InfoPanel color="danger" message={errorText ?? ""} ></InfoPanel>}
          </Col>
        </Row>
        {(infoValue) &&
          <Row>
            {renderOibServiceInfo(false, 4, infoValue.street)}
            {renderOibServiceInfo(true, 4, infoValue.houseNumber, infoValue.houseNumberId === undefined)}
          </Row>
        }
        {children}
      </FormCard>
    </>
  );
}